"use client";

import { useEffect, useRef, useState } from "react";
import { useRealtimeKitMeeting, useRealtimeKitSelector } from "@cloudflare/realtimekit-react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";

import { useClassroomStore } from "../store/useClassroomStore";
import { useMicrophoneVolume } from "../hooks/useMicrophoneVolume";
import { isScreenShareSupportedAsync } from "../utils/screenShareUtils";
import { getRecordingManager } from "../utils/recordingUtils";

import LeaveCallDialog from "./LeaveCallDialog";
import { ActivitiesDialog } from "./ActivitiesDialog";
import LessonCountdownTimer from "./LessonCountdownTimer";
import SpeakingIndicator from "./SpeakingIndicator";

import useUserStore from "@/features/auth/store/useUserStore.ts";
import PATHS from "@/routes/paths.ts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface DeviceItemProps {
  device: { deviceId: string; label: string };
  selected: boolean;
  type: "microphone" | "camera" | "speaker";
  onClick: () => void;
  isPending?: boolean;
}

function DeviceItem({ device, selected, type, onClick, isPending }: DeviceItemProps) {
  const { t } = useTranslation(["common", "classroom"]);

  const getIcon = () => {
    switch (type) {
      case "microphone":
        return "lucide:mic";
      case "camera":
        return "lucide:video";
      case "speaker":
        return "lucide:volume-2";
    }
  };

  const getDefaultLabel = () => {
    switch (type) {
      case "microphone":
        return t("common:microphone");
      case "camera":
        return t("common:camera");
      case "speaker":
        return t("common:speaker" as any);
    }
  };

  const deviceLabel = device.label || `${getDefaultLabel()} ${device.deviceId.slice(0, 5)}`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.button
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all",
              "hover:bg-side-accent/50",
              selected && "bg-side-accent",
              isPending && "opacity-60",
            )}
            disabled={isPending}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={onClick}
          >
            <div
              className={cn(
                "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl",
                selected ? "bg-accent-foreground/10" : "bg-background",
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4",
                  selected ? "text-accent-foreground" : "text-muted-foreground",
                )}
                icon={getIcon()}
              />
            </div>

            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span
                className={cn(
                  "block truncate text-sm",
                  selected ? "text-accent-foreground font-medium" : "text-muted-foreground",
                )}
              >
                {deviceLabel}
              </span>

              {selected && (
                <motion.div
                  animate={{ scale: 1 }}
                  className="flex-shrink-0"
                  initial={{ scale: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  <Icon className="text-accent-foreground h-4 w-4" icon="lucide:check" />
                </motion.div>
              )}
            </div>
          </motion.button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{deviceLabel}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function MeetingControls() {
  const { t } = useTranslation(["common", "classroom"]);

  // Use Cloudflare Realtime Kit hooks instead of VideoSDK
  const { meeting } = useRealtimeKitMeeting();

  // Get local user's media state using Cloudflare selectors
  const isAudioEnabled = useRealtimeKitSelector((m) => m.self?.audioEnabled ?? false);
  const isVideoEnabled = useRealtimeKitSelector((m) => m.self?.videoEnabled ?? false);
  const isScreenSharing = useRealtimeKitSelector((m) => m.self?.screenShareEnabled ?? false);

  const navigate = useNavigate();
  const { workspaceId, lessonId } = useParams();
  const { user } = useUserStore();

  // Use store for device selection and other non-media state
  const {
    selectedWebcam,
    setSelectedWebcam,
    selectedMic,
    setSelectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    presenterId,
    isChatOpen,
    toggleChat,
    setShouldMaintainConnection,
    unreadMessageCount,
    extensionInfo,
    setExtensionDialogOpen,
    lessonEndTime,
    meetingId,
    // Recording state
    isRecording,
    setIsRecording,
    setRecordingStartTime,
    setRecordingBlob,
  } = useClassroomStore();

  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isActivitiesDialogOpen, setIsActivitiesDialogOpen] = useState(false);

  // Track if device change is pending
  const [pendingMicChange, setPendingMicChange] = useState<string | null>(null);
  const micChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if screen sharing is supported on this device
  const [screenShareSupported, setScreenShareSupported] = useState<boolean>(false);

  // Check if recording is supported
  const [recordingSupported, setRecordingSupported] = useState<boolean>(false);

  // Get microphone volume and speaking state
  const { isSpeaking } = useMicrophoneVolume();

  // Device lists state
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);

  // Computed property to check if screen sharing is active
  const isScreenSharingActive = presenterId !== null;
  const isLocalScreenSharing = isScreenSharing;

  // Function to fetch devices using Cloudflare API
  const fetchDevices = async () => {
    if (!meeting) return;

    try {
      // Get all devices using Cloudflare API
      const audioDevices = await meeting.self.getAudioDevices();
      const videoDevices = await meeting.self.getVideoDevices();
      const speakerDevices = await meeting.self.getSpeakerDevices();

      setAudioInputs(audioDevices);
      setVideoInputs(videoDevices);
      setAudioOutputs(speakerDevices);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  // Fetch devices on mount and when meeting changes
  useEffect(() => {
    fetchDevices();
  }, [meeting]);

  // Function to change speaker (audio output)
  const changeSpeakerDevice = async (deviceId: string) => {
    try {
      // Update the selected speaker in store
      setSelectedSpeaker(deviceId);

      if (meeting) {
        // Find the speaker device object
        const speakerDevice = audioOutputs.find((device) => device.deviceId === deviceId);

        if (speakerDevice) {
          // Use Cloudflare's native device switching
          await meeting.self.setDevice(speakerDevice);
          console.log("Speaker device changed to:", deviceId);
        }
      }
    } catch (error) {
      console.error("Error changing speaker device:", error);
    }
  };

  // Check screen share support on mount
  useEffect(() => {
    const checkScreenShareSupport = async () => {
      try {
        const supported = await isScreenShareSupportedAsync();
        const hasGetDisplayMedia = !!navigator.mediaDevices?.getDisplayMedia;

        setScreenShareSupported(supported);

        if (!supported) {
          console.log(`Screen sharing not supported. Reason: API missing = ${!hasGetDisplayMedia}`);
        }
        console.log("=== End Screen Sharing Detection ===");
      } catch (error) {
        console.error("Error checking screen share support:", error);
        setScreenShareSupported(false);
      }
    };

    checkScreenShareSupport();
  }, []);

  // Check recording support on mount
  useEffect(() => {
    const checkRecordingSupport = () => {
      // const recordingManager = getRecordingManager();
      // const supported = recordingManager.isRecordingSupported();

      setRecordingSupported(false);
    };

    checkRecordingSupport();
  }, []);

  const handleWebcamChange = async (deviceId: string) => {
    // Check if we're in PiP transition - if so, only update selection but don't change device
    const { isTransitioningFromPiP, isPipMode } = useClassroomStore.getState();

    setSelectedWebcam(deviceId);

    if (isTransitioningFromPiP || isPipMode) {
      console.log("Skipping webcam change - PiP mode or transitioning, only updating selection");

      return;
    }

    try {
      // Use Cloudflare's device change method - find the device object first
      const device = videoInputs.find((d) => d.deviceId === deviceId);

      if (device) {
        await meeting.self.setDevice(device);
      }
    } catch (error) {
      console.error("Error changing webcam:", error);
    }
  };

  const handleMicChange = async (deviceId: string) => {
    // Check if we're in PiP transition - if so, only update selection but don't change device
    const { isTransitioningFromPiP, isPipMode } = useClassroomStore.getState();

    setSelectedMic(deviceId);

    if (isTransitioningFromPiP || isPipMode) {
      console.log("Skipping mic change - PiP mode or transitioning, only updating selection");

      return;
    }

    setPendingMicChange(deviceId);

    if (micChangeTimeoutRef.current) {
      clearTimeout(micChangeTimeoutRef.current);
    }

    micChangeTimeoutRef.current = setTimeout(async () => {
      try {
        // Find the device object first
        const device = audioInputs.find((d) => d.deviceId === deviceId);

        if (device) {
          await meeting.self.setDevice(device);
        }
        console.log("changed mic to: ", device);
        setPendingMicChange(null);
      } catch (error) {
        console.error("Error changing microphone:", error);
        setPendingMicChange(null);
      }
    }, 300);
  };

  const handleScreenShare = async () => {
    try {
      if (isLocalScreenSharing) {
        await meeting.self.disableScreenShare();
      } else {
        // Enable screen share without options
        await meeting.self.enableScreenShare();
      }
    } catch (error) {
      console.error("Error toggling screen share:", error);
    }
  };

  const handleLeaveClick = () => {
    setIsLeaveDialogOpen(true);
  };

  const shouldShowExtendButton =
    user?.role === "tutor" &&
    extensionInfo &&
    extensionInfo.eventData.allow_extension &&
    extensionInfo.roomId === meetingId;

  const handleLeaveConfirm = async () => {
    setShouldMaintainConnection(false);

    try {
      await meeting.leave();
    } catch (error) {
      console.error("Error leaving meeting:", error);
    }

    setIsLeaveDialogOpen(false);
    if (user && workspaceId && lessonId) {
      if (user.role === "tutor") {
        navigate(`${PATHS.TUTOR.WORKSPACES.WORKSPACES}/${workspaceId}/lessons/${lessonId}/details`);
      } else {
        navigate(
          `${PATHS.STUDENT.WORKSPACES.WORKSPACES}/${workspaceId}/lessons/${lessonId}/details`,
        );
      }
    }
  };

  const handleToggleMic = async () => {
    // Optimistic update - change UI immediately
    const newMicState = !isAudioEnabled;

    if (meeting) {
      try {
        // Use Cloudflare's toggle methods
        if (newMicState) {
          await meeting.self.enableAudio();
        } else {
          await meeting.self.disableAudio();
        }
      } catch (error) {
        console.error("Error toggling mic:", error);
        // Revert optimistic update on failure
      }
    }
  };

  const handleToggleWebcam = async () => {
    // Optimistic update - change UI immediately
    const newWebcamState = !isVideoEnabled;

    if (meeting) {
      try {
        // Use Cloudflare's toggle methods
        if (newWebcamState) {
          await meeting.self.enableVideo();
        } else {
          await meeting.self.disableVideo();
        }
      } catch (error) {
        console.error("Error toggling webcam:", error);
        // Revert optimistic update on failure
      }
    }
  };

  // Recording handlers
  const handleStartRecording = async () => {
    try {
      const recordingManager = getRecordingManager();

      await recordingManager.startRecording();

      setIsRecording(true);
      setRecordingStartTime(Date.now());
      console.log("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const handleStopRecording = async () => {
    try {
      const recordingManager = getRecordingManager();
      const blob = await recordingManager.stopRecording();

      setIsRecording(false);
      setRecordingStartTime(null);
      setRecordingBlob(blob);

      if (blob) {
        // Auto-download the recording
        recordingManager.downloadRecording(blob, `lesson-recording-${Date.now()}`);
      }

      console.log("Recording stopped");
    } catch (error) {
      console.error("Error stopping recording:", error);
      setIsRecording(false);
      setRecordingStartTime(null);
    }
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (micChangeTimeoutRef.current) {
        clearTimeout(micChangeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <TooltipProvider delayDuration={400}>
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="bg-background/95 flex w-full items-center justify-between gap-1 rounded-xl p-1 px-6 shadow-2xl backdrop-blur-md"
          initial={{ opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          {/* Left side - Countdown Timer */}
          <div className="flex items-center">
            <LessonCountdownTimer endTime={lessonEndTime} />
          </div>
          <div className="flex items-center gap-1">
            {/* Microphone group */}
            <div
              className={cn(
                "group flex items-center rounded-xl transition-colors",
                "h-10 gap-1 px-1.5 md:h-11", // fixed height, small horizontal padding - 20% smaller
                isAudioEnabled
                  ? "bg-muted hover:bg-muted/80"
                  : "bg-destructive hover:bg-destructive/90",
              )}
            >
              {/* Mic toggle */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    className="flex h-8 w-8 items-center justify-center rounded-xl focus:outline-none"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleToggleMic}
                  >
                    <Icon
                      className={cn(
                        "transition-colors",
                        isAudioEnabled ? "text-accent-foreground" : "text-destructive-foreground",
                      )}
                      height="16"
                      icon={isAudioEnabled ? "lucide:mic" : "lucide:mic-off"}
                      width="16"
                    />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isAudioEnabled ? t("classroom:muteMic") : t("classroom:unmuteMic")}</p>
                </TooltipContent>
              </Tooltip>

              {/* Dropdown trigger */}
              <Popover>
                <PopoverTrigger asChild>
                  <motion.button
                    className="flex h-8 w-8 items-center justify-center rounded-xl focus:outline-none"
                    initial={{ opacity: 0.8 }}
                    whileHover={{ scale: 1.1, opacity: 1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {isSpeaking ? (
                      <div className="relative flex h-fit items-end gap-0.5">
                        <SpeakingIndicator isSpeaking={true} variant={"dots"} />
                      </div>
                    ) : (
                      <Icon
                        className="text-muted-foreground"
                        height="14"
                        icon="lucide:chevron-up"
                        width="14"
                      />
                    )}
                  </motion.button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  className="border-border/50 bg-background/95 w-80 rounded-xl p-0 shadow-xl backdrop-blur-md"
                  sideOffset={10}
                >
                  <Tabs className="w-full" defaultValue="microphone">
                    <TabsList className="bg-side-accent/30 grid w-full grid-cols-2">
                      <TabsTrigger className="data-[state=active]:bg-background" value="microphone">
                        {t("common:microphone")}
                      </TabsTrigger>
                      <TabsTrigger className="data-[state=active]:bg-background" value="speaker">
                        {t("common:speaker" as any)}
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent className="p-2" value="microphone">
                      <div className="mb-2 px-2">
                        <h3 className="text-accent-foreground text-sm font-medium">
                          {t("classroom:selectMicrophone")}
                        </h3>
                      </div>
                      <ScrollArea className="h-[200px] max-w-full [&_>_div_>_div]:block!">
                        <div className="max-w-full space-y-1 p-1">
                          {audioInputs.length > 0 ? (
                            audioInputs.map((mic) => (
                              <DeviceItem
                                key={mic.deviceId}
                                device={mic}
                                isPending={pendingMicChange === mic.deviceId}
                                selected={selectedMic === mic.deviceId}
                                type="microphone"
                                onClick={() => handleMicChange(mic.deviceId)}
                              />
                            ))
                          ) : (
                            <div className="text-muted-foreground py-8 text-center text-sm">
                              {t("classroom:noMicrophonesFound")}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </TabsContent>

                    <TabsContent className="p-2" value="speaker">
                      <div className="mb-2 px-2">
                        <h3 className="text-accent-foreground text-sm font-medium">
                          {t("classroom:selectSpeaker" as any)}
                        </h3>
                      </div>
                      <ScrollArea className="h-[200px] max-w-full [&_>_div_>_div]:block!">
                        <div className="max-w-full space-y-1 p-1">
                          {audioOutputs.length > 0 ? (
                            audioOutputs.map((speaker) => (
                              <DeviceItem
                                key={speaker.deviceId}
                                device={speaker}
                                selected={selectedSpeaker === speaker.deviceId}
                                type="speaker"
                                onClick={() => changeSpeakerDevice(speaker.deviceId)}
                              />
                            ))
                          ) : (
                            <div className="text-muted-foreground py-8 text-center text-sm">
                              {t("classroom:noSpeakersFound" as any)}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </PopoverContent>
              </Popover>
            </div>

            {/* Camera group */}
            <div
              className={cn(
                "group flex items-center rounded-xl transition-colors",
                "h-10 gap-1 px-1.5 md:h-11",
                isVideoEnabled
                  ? "bg-muted hover:bg-muted/80"
                  : "bg-destructive hover:bg-destructive/90",
              )}
            >
              {/* Camera toggle */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    className="flex h-8 w-8 items-center justify-center rounded-xl focus:outline-none"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleToggleWebcam}
                  >
                    <Icon
                      className={cn(
                        "transition-colors",
                        isVideoEnabled ? "text-accent-foreground" : "text-destructive-foreground",
                      )}
                      height="16"
                      icon={isVideoEnabled ? "lucide:video" : "lucide:video-off"}
                      width="16"
                    />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {isVideoEnabled ? t("classroom:disableCamera") : t("classroom:enableCamera")}
                  </p>
                </TooltipContent>
              </Tooltip>

              {/* Dropdown trigger */}
              <Popover>
                <PopoverTrigger asChild>
                  <motion.button
                    className="flex h-8 w-8 items-center justify-center rounded-xl focus:outline-none"
                    initial={{ opacity: 0.8 }}
                    whileHover={{ scale: 1.1, opacity: 1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Icon
                      className="text-muted-foreground"
                      height="14"
                      icon="lucide:chevron-up"
                      width="14"
                    />
                  </motion.button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  className="border-border/50 bg-background/95 w-80 rounded-xl p-0 shadow-xl backdrop-blur-md"
                  sideOffset={10}
                >
                  <div className="p-2">
                    <div className="mb-2 px-2">
                      <h3 className="text-accent-foreground text-sm font-medium">
                        {t("classroom:selectCamera")}
                      </h3>
                    </div>
                    <ScrollArea className="h-[200px] max-w-full [&_>_div_>_div]:block!">
                      <div className="max-w-full space-y-1 p-1">
                        {videoInputs.length > 0 ? (
                          videoInputs.map((webcam) => (
                            <DeviceItem
                              key={webcam.deviceId}
                              device={webcam}
                              selected={selectedWebcam === webcam.deviceId}
                              type="camera"
                              onClick={() => handleWebcamChange(webcam.deviceId)}
                            />
                          ))
                        ) : (
                          <div className="text-muted-foreground py-8 text-center text-sm">
                            {t("classroom:noCamerasFound")}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Screen share button - only show if supported */}
            {screenShareSupported && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-xl transition-all md:h-11 md:w-11",
                      isLocalScreenSharing
                        ? "bg-primary hover:bg-primary/90"
                        : isScreenSharingActive
                          ? "bg-muted/50 cursor-not-allowed opacity-60"
                          : "bg-muted hover:bg-muted/80",
                    )}
                    disabled={isScreenSharingActive && !isLocalScreenSharing}
                    whileHover={{
                      scale: isScreenSharingActive && !isLocalScreenSharing ? 1 : 1.05,
                    }}
                    whileTap={{ scale: isScreenSharingActive && !isLocalScreenSharing ? 1 : 0.95 }}
                    onClick={handleScreenShare}
                  >
                    <Icon
                      className={cn(
                        "transition-colors",
                        isLocalScreenSharing ? "text-primary-foreground" : "text-accent-foreground",
                      )}
                      height="16"
                      icon={isLocalScreenSharing ? "lucide:monitor-off" : "lucide:monitor"}
                      width="16"
                    />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {isLocalScreenSharing
                      ? t("classroom:stopScreenShare")
                      : isScreenSharingActive
                        ? t("classroom:screenShareInUse")
                        : t("classroom:screenShare")}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Recording button - only show if supported */}
            {recordingSupported && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-xl transition-all md:h-11 md:w-11",
                      isRecording
                        ? "bg-destructive hover:bg-destructive/90"
                        : "bg-muted hover:bg-muted/80",
                    )}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleToggleRecording}
                  >
                    <Icon
                      className={cn(
                        "transition-colors",
                        isRecording ? "text-destructive-foreground" : "text-accent-foreground",
                      )}
                      height="16"
                      icon={isRecording ? "lucide:square" : "lucide:circle"}
                      width="16"
                    />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isRecording ? t("stopRecording" as any) : t("startRecording" as any)}</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Chat toggle button */}
            <motion.div className="relative">
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    animate={isChatOpen ? { rotate: [0, -5, 5, 0] } : {}}
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-xl transition-all md:h-11 md:w-11",
                      isChatOpen ? "bg-primary hover:bg-primary/90" : "bg-muted hover:bg-muted/80",
                    )}
                    transition={{ duration: 0.4 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleChat}
                  >
                    <motion.div
                      animate={{ rotate: isChatOpen ? 0 : 0 }}
                      className={"flex items-center"}
                      transition={{ duration: 0.3 }}
                    >
                      <Icon
                        className={cn(
                          "transition-colors",
                          isChatOpen ? "text-primary-foreground" : "text-accent-foreground",
                        )}
                        height="16"
                        icon={isChatOpen ? "lucide:message-square-text" : "lucide:message-square"}
                        width="16"
                      />
                    </motion.div>
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("classroom:lessonChat")}</p>
                </TooltipContent>
              </Tooltip>

              {/* Unread message count badge */}
              {unreadMessageCount > 0 && !isChatOpen && (
                <motion.div
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-destructive text-destructive-foreground absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold shadow-sm"
                  initial={{ scale: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                </motion.div>
              )}
            </motion.div>
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  className="bg-destructive hover:bg-destructive/90 flex h-10 w-10 items-center justify-center rounded-xl transition-all md:h-11 md:w-11"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLeaveClick}
                >
                  <Icon
                    className="text-destructive-foreground"
                    height="18"
                    icon="lucide:phone-off"
                    width="18"
                  />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("classroom:leaveCall")}</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-1">
            {/* Extend Lesson button (appears after lesson ending soon) */}
            {shouldShowExtendButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    className="bg-accent-foreground hover:bg-accent-foreground/75 flex h-10 w-10 items-center justify-center rounded-xl transition-all md:h-11 md:w-11"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setExtensionDialogOpen(true)}
                  >
                    <Icon className="text-primary" height="16" icon="lucide:clock" width="16" />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("classroom:extendLesson")}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {/* Activities button - only for tutors */}
            {user?.role === "tutor" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.button
                    className="bg-muted hover:bg-muted/80 flex h-10 w-10 items-center justify-center rounded-xl transition-all md:h-11 md:w-11"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsActivitiesDialogOpen(true)}
                  >
                    <Icon
                      className="text-primary-foreground"
                      height="16"
                      icon="lucide:activity"
                      width="16"
                    />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("classroom:activities")}</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Leave meeting - moved to the end */}
          </div>
        </motion.div>
      </TooltipProvider>

      {/* Leave Call Dialog */}
      <LeaveCallDialog
        isOpen={isLeaveDialogOpen}
        onClose={() => setIsLeaveDialogOpen(false)}
        onConfirm={handleLeaveConfirm}
      />

      {/* Activities Dialog */}
      <ActivitiesDialog
        isOpen={isActivitiesDialogOpen}
        onClose={() => setIsActivitiesDialogOpen(false)}
      />
    </>
  );
}
